import { ValidationErrors } from '@angular/forms';
import { ValidatorFn, AbstractControl, FormGroup } from '@angular/forms';
import urlRegex from 'url-regex';
import dayjs from 'dayjs';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import { parseStoreLink } from './store-link.utils';

dayjs.extend(isLeapYear);

export function nonZeroAmountValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === 0 || value === '0' || parseFloat(value) === 0) {
      return { zeroAmount: true };
    }
    return null;
  };
}

function isEmptyInputValue(value: any): boolean {
  // we don't check for string here so it also works with arrays
  return value == null || value.length === 0;
}
const EMAIL_REGEXP =
  /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

export const URL_REGEXP = new RegExp(
  '^' +
    '(' +
    '(?:' +
    '(?:' +
    '[a-z0-9\\u00a1-\\uffff]' +
    '[a-z0-9\\u00a1-\\uffff_-]{4,}' +
    '[a-z0-9\\u00a1-\\uffff]' +
    ')' +
    ')' +
    '|' +
    '(' +
    '(?:(?:(?:https?):)?\\/\\/|)' +
    '(?:' +
    '(?:' +
    '(?:' +
    '[a-z0-9\\u00a1-\\uffff]' +
    '[a-z0-9\\u00a1-\\uffff_-]{0,62}' +
    ')?' +
    '[a-z0-9\\u00a1-\\uffff]\\.' +
    ')+' +
    '(?:[a-z\\u00a1-\\uffff]{2,}\\.?)' +
    ')' +
    ')' +
    ')' +
    '$',
  'i',
);

export const HTTPS_URL_REGEX =
  /^https:\/\/(?!https?:\/\/)(?!\/)(?:(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,}|xn--[a-z0-9-]{2,})|(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3})(?::\d{2,5})?(?:\/[^\s?#]*)?(?:\?[^\s#]*)?(?:#[^\s]*)?$/i;

export class CustomValidators {
  static matchFieldsValidator(
    controlName: string,
    matchingControlName: string,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const field = control.get(controlName).value;
      const matchingField = control.get(matchingControlName).value;

      if (!field || !matchingField) {
        return null;
      }

      if (field !== matchingField) {
        control.get(controlName).setErrors({ matchFields: true });
      } else {
        control.get(controlName).setErrors({ matchFields: null });
        control
          .get(controlName)
          .updateValueAndValidity({ emitEvent: false, onlySelf: true });
        return null;
      }
    };
  }

  static discountValidator(
    price: string,
    discount: string,
    discountType: string,
  ): ValidatorFn {
    return (fg: FormGroup) => {
      if (fg.get(discountType).value === 'MONETARY') {
        if (Number(fg.get(discount).value) >= Number(fg.get(price).value)) {
          return { invalidDiscount: true };
        }
      } else {
        return null;
      }
    };
  }

  static checkForOnlySpaces(
    fieldName: string,
    allowEmptyValue = false,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (allowEmptyValue && control.get(fieldName).value === '') {
        return null;
      }
      if (!allowEmptyValue && !control.get(fieldName).value) {
        return null;
      }
      const isWhitespace =
        (control.get(fieldName).value || '').trim().length === 0;
      if (isWhitespace) {
        control.get(fieldName).setErrors({ whitespace: true });
      } else {
        return null;
      }
    };
  }

  static noWhitespaceValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  static timeControlValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const parsedDate =
      control.value instanceof Date
        ? control.value
        : new Date(control.value as string);
    return Number.isNaN(parsedDate.getTime()) ? { invalidTime: true } : null;
  }

  static luhnValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const isValid = CustomValidators.luhnCheck(control.value);
      return isValid ? null : { luhnCheck: true };
    };
  }

  static luhnCheck = (cardNumber: string): boolean => {
    if (!cardNumber.length) {
      return;
    }

    // Remove all whitespaces from card number.
    cardNumber = cardNumber.replace(/\s/g, '');

    // 1. Remove last digit;
    const lastDigit = Number(cardNumber[cardNumber.length - 1]);

    // 2. Reverse card number
    const reverseCardNumber = cardNumber
      .slice(0, cardNumber.length - 1)
      .split('')
      .reverse()
      .map((x) => Number(x));
    let sum = 0;

    // 3. + 4. Multiply by 2 every digit on odd position. Subtract 9 if digit > 9
    for (let i = 0; i <= reverseCardNumber.length - 1; i += 2) {
      reverseCardNumber[i] = reverseCardNumber[i] * 2;
      if (reverseCardNumber[i] > 9) {
        reverseCardNumber[i] = reverseCardNumber[i] - 9;
      }
    }

    // 5. Make the sum of obtained values from step 4.
    sum = reverseCardNumber.reduce((acc, currValue) => acc + currValue, 0);

    // 6. Calculate modulo 10 of the sum from step 5. and the last digit. If it's 0, you have a valid card number :)
    return (sum + lastDigit) % 10 === 0;
  };
  static required(control: AbstractControl): ValidationErrors | null {
    return isEmptyInputValue(control.value) || !control.value
      ? { required: true }
      : null;
  }
  static email(control: AbstractControl): ValidationErrors | null {
    if (isEmptyInputValue(control.value)) {
      return null; // don't validate empty values to allow optional controls
    }
    return EMAIL_REGEXP.test(control.value.trim()) ? null : { email: true };
  }
  static stockemail(control: AbstractControl): ValidationErrors | null {
    if (isEmptyInputValue(control.value)) {
      return null; // don't validate empty values to allow optional controls
    }
    return EMAIL_REGEXP.test(control.value.trim())
      ? null
      : { stockemail: true };
  }

  static containsUrl(control: AbstractControl): ValidationErrors | null {
    if (isEmptyInputValue(control.value)) {
      return null; // don't validate empty values to allow optional controls
    }
    const value: string = control.value.trim();
    return urlRegex({ strict: false }).test(value)
      ? { containsUrl: true }
      : null;
  }

  static pattern(pattern: string | RegExp): ValidatorFn {
    if (!pattern) {
      return null;
    }
    let regex: RegExp;
    let regexStr: string;
    if (typeof pattern === 'string') {
      regexStr = '';

      if (pattern.charAt(0) !== '^') {
        regexStr += '^';
      }

      regexStr += pattern;

      if (pattern.charAt(pattern.length - 1) !== '$') {
        regexStr += '$';
      }

      regex = new RegExp(regexStr);
    } else {
      regexStr = pattern.toString();
      regex = pattern;
    }
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) {
        return null; // don't validate empty values to allow optional controls
      }
      const value: string = control.value.trim();
      return regex.test(value)
        ? null
        : { pattern: { requiredPattern: regexStr, actualValue: value } };
    };
  }

  static url(control: AbstractControl): ValidationErrors | null {
    if (isEmptyInputValue(control.value)) {
      return null; // don't validate empty values to allow optional controls
    }
    return URL_REGEXP.test(control.value.trim()) ? null : { url: true };
  }

  /**
   * Kiosk store entry validator.
   * Accepts:
   * - bare aliases (e.g. "my-store")
   * - tenant store URLs (<alias>.<domainUrl>) with optional path/query/hash (e.g. "https://my-store.example.com/#l/500")
   *
   * GO Kiosk can opt-in to support custom/external domains.
   */
  static storeUrlOrAlias(
    domainUrl: string,
    allowExternalDomains: boolean = false,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) {
        return null; // don't validate empty values to allow optional controls
      }
      const value: string = String(control.value ?? '').trim();
      if (!value) {
        return null;
      }
      return parseStoreLink(value, domainUrl, { allowExternalDomains })
        ? null
        : { url: true };
    };
  }

  private static hasPhoneValue(
    phoneNumber: string,
    selectedCountryCode: string | undefined,
  ): boolean {
    const trimmedPhoneNumber = phoneNumber?.trim() ?? '';
    if (!trimmedPhoneNumber) {
      return false;
    }
    if (
      selectedCountryCode &&
      trimmedPhoneNumber === `+${selectedCountryCode}`
    ) {
      return false;
    }
    return true;
  }

  static phoneLengthValidator(
    minLength: number,
    requiredResolver: () => boolean,
    selectedCountryCodeResolver: () => string | undefined,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const phoneNumber = (control.value || '').trim();
      const required = requiredResolver();
      const selectedCountryCode = selectedCountryCodeResolver();
      const hasPhoneValue = CustomValidators.hasPhoneValue(
        phoneNumber,
        selectedCountryCode,
      );

      if (!required && !hasPhoneValue) {
        return null;
      }

      if (required && !hasPhoneValue) {
        return { required: true };
      }

      if (phoneNumber.length < minLength) {
        return {
          minlength: {
            requiredLength: minLength,
            actualLength: phoneNumber.length,
          },
        };
      }

      return null;
    };
  }

  static maxLength20DigitsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === '') {
        return null;
      }

      const valueStr: string = control.value.toString().trim();
      if (!/^\d+$/.test(valueStr)) {
        return { invalidNumber: true };
      }

      if (valueStr.length > 20) {
        return { maxLengthExceeded: true };
      }

      return null;
    };
  }

  static validateUniqueName(existingNames: string[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (existingNames.includes(control.value)) {
        return { uniqueName: { message: 'Name is already in use' } };
      } else {
        return null;
      }
    };
  }

  static validateMinMaxPersons(group: AbstractControl) {
    const minPerson = group.get('minPerson')?.value;
    const maxPerson = group.get('maxPerson')?.value;

    if (minPerson && maxPerson && Number(maxPerson) < Number(minPerson)) {
      group.get('maxPerson')?.setErrors({ maxLessThanMin: true });
      return { maxLessThanMin: true };
    }
    return null;
  }

  static httpsUrlOrBlank(control: AbstractControl): ValidationErrors | null {
    const v = (control.value ?? '').trim();
    if (!v || v === 'https://') return null; // allow empty/prefix-only
    return HTTPS_URL_REGEX.test(v) ? null : { url: true };
  }

  static dateValidator(formGroup: AbstractControl): ValidationErrors | null {
    const day = formGroup.get('birthdayDay')?.value;
    const month = formGroup.get('birthdayMonth')?.value;
    const year = formGroup.get('birthdayYear')?.value;

    // If all fields are empty, date of birth is optional - no validation errors
    if (!day && !month && !year) {
      return null;
    }

    let errors: any = {};

    // If any field is provided, check for partial date entry
    if (day || month || year) {
      // Check for missing day when month/year provided
      if (!day && (month || year)) {
        errors.dayMissing = true;
      }
      // Check for missing month when day/year provided
      if (!month && (day || year)) {
        errors.monthMissing = true;
      }
    }

    // If day and month are provided, validate the date logic
    if (day && month) {
      const dayNum = Number(day);
      const monthNum = Number(month);
      const yearNum = year ? Number(year) : 1896; // Use 1896 if year not provided

      // Validate month range
      if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
        errors.invalidDate = true;
      }

      // Validate day range
      else if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
        errors.invalidDate = true;
      }

      // Validate day/month combinations
      else {
        const daysInMonth = CustomValidators.getDaysInMonth(monthNum, yearNum);
        if (dayNum > daysInMonth) {
          errors.invalidDate = true;
        }
      }
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }
  private static getDaysInMonth(month: number, year: number): number {
    return dayjs(`${year}-${month}-01`).daysInMonth();
  }

  private static isLeapYear(year: number): boolean {
    return dayjs(`${year}-01-01`).isLeapYear();
  }
}
